
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { format, subMonths } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

interface SalaryDeductionsTabProps {
  showActions?: boolean;
}

export const SalaryDeductionsTab = ({ showActions = true }: SalaryDeductionsTabProps) => {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");

  // Generate last 24 months for the filter
  const monthOptions = Array.from({ length: 24 }, (_, i) => {
    const date = subMonths(new Date(), i);
    return {
      value: format(date, "yyyy-MM"),
      label: format(date, "MMMM yyyy", { locale: fr }),
    };
  });

  const { data: deductions = [], refetch } = useQuery({
    queryKey: ["salary-deductions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("counting_discrepancies")
        .select("*")
        .eq("created_by", user.id)
        .eq("is_salary_deduction", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleMarkAsProcessed = async (discrepancyId: string) => {
    try {
      const { error } = await supabase
        .from("counting_discrepancies")
        .update({ is_salary_deduction_processed: true })
        .eq("id", discrepancyId);

      if (error) throw error;
      await refetch();
      toast.success("Retenue sur salaire marquée comme effectuée");
    } catch (error) {
      console.error("Error marking salary deduction as processed:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  // Filter the deductions based on selected filters
  const filteredDeductions = deductions.filter((deduction) => {
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "processed" && deduction.is_salary_deduction_processed) ||
      (statusFilter === "pending" && !deduction.is_salary_deduction_processed);

    const deductionMonth = format(new Date(deduction.counting_date), "yyyy-MM");
    const matchesMonth = monthFilter === "all" || deductionMonth === monthFilter;

    return matchesStatus && matchesMonth;
  });

  const columns = [
    {
      id: "date",
      label: "Date",
      cell: (row: any) => format(new Date(row.counting_date), "d MMMM yyyy", { locale: fr }),
    },
    {
      id: "employee",
      label: "Employé",
      cell: (row: any) => row.salary_deduction_employee || "Non spécifié",
    },
    {
      id: "difference",
      label: "Montant",
      cell: (row: any) => `${row.difference_amount.toLocaleString()} FCFA`,
    },
    {
      id: "status",
      label: "Statut",
      cell: (row: any) => row.is_salary_deduction_processed ? "Effectué" : "En attente",
    },
    ...(showActions ? [{
      id: "actions",
      label: "Actions",
      cell: (row: any) => !row.is_salary_deduction_processed && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleMarkAsProcessed(row.id)}
          className="text-green-600 hover:text-green-700"
        >
          <Check className="w-4 h-4 mr-2" />
          Marquer comme effectuée
        </Button>
      ),
    }] : []),
  ];

  return (
    <Card className="p-6">
      <div className="flex gap-4 mb-6">
        <Select
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="processed">Effectué</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={monthFilter}
          onValueChange={setMonthFilter}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrer par mois" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {monthOptions.map((month) => (
              <SelectItem key={month.value} value={month.value}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={filteredDeductions}
      />
    </Card>
  );
};
